import React, { useCallback, useState } from 'react';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import Input, { InputProps } from 'components/commercetools-ui/atoms/input';
import PhoneInput from 'components/commercetools-ui/atoms/input-phone';
import { Address } from '../../types';
import { Fields, FieldsOptions } from '../address-form/types';

interface Props {
  address: Partial<Address>;
  className?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPlaceChanged?: (place: google.maps.places.PlaceResult | undefined, isShippingAddress: boolean) => void;
  fields: (options: FieldsOptions) => Fields[];
  isShippingAddress?: boolean;
}

const libraries: 'places'[] = ['places'];

export const CustomAddress: React.FC<Props> = ({
  address,
  onChange,
  className,
  fields,
  isShippingAddress,
  onPlaceChanged,
}) => {
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});

  const [autoComplete, setAutoComplete] = useState<google.maps.places.Autocomplete | null>(null);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY as string,
    libraries,
  });

  const getComponent = useCallback(
    ({ name, validate, required, specificErrorMessage, isAutoComplete, ...props }: Fields) => {
      const value = address[name as keyof Omit<Address, 'addressId'>] as string;
      const isEmpty = !value;
      const isInvalid = validate ? !validate(value) : false;
      const isTouched = touchedFields[name];

      let errorMessage: string | undefined;
      if (required && isEmpty && isTouched) {
        errorMessage = 'Requested field';
      } else if (!isEmpty && isInvalid && specificErrorMessage) {
        errorMessage = specificErrorMessage;
      }

      const handleBlur = (_e: React.FocusEvent<HTMLInputElement>) => {
        setTouchedFields((prev) => ({ ...prev, [name]: true }));
      };

      const inputProps = {
        ...props,
        name,
        required,
        value,
        labelPosition: 'top' as InputProps['labelPosition'],
        isValid: !isEmpty && !isInvalid,
        error: errorMessage,
        onChange,
        onBlur: handleBlur,
        hideCheckIcon: true,
        className: errorMessage ? '!border-feedback-error' : '',
      };

      // Phone field: use local PhoneInput (no external mask lib)
      if (name === 'phone') {
        return (
          <PhoneInput
            {...inputProps}
            value={value}
            onChange={(fieldName, fieldValue) => {
              if (onChange) {
                const event = {
                  target: {
                    name: fieldName,
                    value: fieldValue,
                  },
                } as React.ChangeEvent<HTMLInputElement>;
                onChange(event);
              }
            }}
            onBlur={handleBlur}
            placeholder={props.placeholder ?? '(212) 555-1234'}
          />
        );
      }

      if (isAutoComplete && isLoaded) {
        return (
          <Autocomplete
            onLoad={(autocomplete) => setAutoComplete(autocomplete)}
            onPlaceChanged={() => {
              onPlaceChanged?.(autoComplete?.getPlace(), !!isShippingAddress);
            }}
            restrictions={{ country: 'US' }}
          >
            <Input {...inputProps} />
          </Autocomplete>
        );
      }

      return <Input {...inputProps} />;
    },
    [isLoaded, address, onChange, touchedFields],
  );

  return (
    <div className={`${className}`}>
      {fields({ enableAddress2: false, onEnableAddress2: () => {}, isShippingAddress }).map(
        ({ render, className, name, ...props }) => (
          <div className={className} key={name}>
            {render && render()}
            {!render && getComponent({ ...props, render, className, name })}
          </div>
        ),
      )}
    </div>
  );
};
